import { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { UserAvatar, Typography, Popover, Box, Link, customTheme, Stack,
    IconButton, MenuIcon, Backdrop, HandbookSearchBar
 } from 'src/components';
import { Profile, HandbookMenu } from 'src/pages';
import logo from 'src/assets/logo-rect.png';
import logoWhite from 'src/assets/logo-rect-white.png';
import { useAuth } from 'src/utils';
import DesktopHeaderMenu from './desktop-header-menu';

export default function DesktopHeader(props) {
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const handbookButtonRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const isHomePageActive = location.pathname == "/";
    const isHandbookActive = location.pathname.includes("handbook");

    const visibleNavItems = props.navItems.filter(
        (item) => !(item.label === 'Chat' && !user)
    );

    const handleHandbookMenuClick = () => {
        props.setHandbookMenuTab('chapters');
    };

    const handleProfileClick = (event) => {
        setProfileAnchorEl(event.currentTarget);
    };

    const handleProfileClose = () => {
        setProfileAnchorEl(null);
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    const profilePopoverOpen = Boolean(profileAnchorEl);

    return (
        <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 2rem',
                height: '60px',
                top: 0,
                zIndex: 100, 
                width: '100%',
                backgroundColor: 'transparent',
                }}>
            
            <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
                <img 
                    src={isHandbookActive || isHomePageActive ? logoWhite : logo} 
                    alt="Logo" 
                    style={{ height: '90%', cursor: 'pointer'}} 
                    onClick={() => handleNavigation('/')}/>

                {isHandbookActive ?
                    <Box sx={{marginLeft: 2, display: 'flex', flexDirection: 'row'}}>

                        <HandbookSearchBar />
                        
                        <IconButton 
                            ref={handbookButtonRef}
                            disableRipple 
                            size="small" 
                            onClick={handleHandbookMenuClick}>
                            <MenuIcon fontSize="small" sx={{color: '#fff'}}/>
                        </IconButton>

                    </Box>
                :<></>}
            </div>

            <div 
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginRight: '16px',
                    width: 'min-content',
                    minWidth: '450px'}}>

                <Stack direction="row" spacing={4}>
                    {visibleNavItems.map((item) => {
                        let isActive = location.pathname.includes(item.path);
                        const isChatItem = item.path.includes("chat");
                        if (isChatItem) {
                            isActive = location.pathname.includes("chat");
                        }

                        return (
                            <Link
                                key={item.label}
                                onClick={() => handleNavigation(item.path)}
                                underline="none"
                                sx={{                                  
                                    color: isActive
                                        ? (isHomePageActive || isHandbookActive ? '#fff' : customTheme.palette.primary.main)
                                        : (isHomePageActive || isHandbookActive ? '#fff' : customTheme.palette.text.main),
                                    px: 1,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: isHomePageActive || isHandbookActive ? '#fff' : customTheme.palette.primary.main,
                                    },
                                    borderBottom: isActive ? '2px solid' : ''
                                }}
                            >
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <Typography variant="subtitle1" fontWeight={isActive ? 700 : 600}>
                                        {item.label}
                                    </Typography>

                                    {isChatItem && props.userHasUnreadChats && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 1,
                                                right: -9,
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: 'secondary.main',
                                            }}
                                        />
                                    )}
                                </Box>
                                
                            </Link>
                        );
                    })}
                    <DesktopHeaderMenu isHomePage={isHomePageActive} isHandbook={isHandbookActive} />
                </Stack>

                <Box
                    role="button"
                    tabIndex={0}
                    onClick={handleProfileClick}
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        marginLeft: '40px',
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: isHomePageActive ? 'rgba(255,255,255,0.8)' : 'grey.400',
                        boxShadow: '0 0 6px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 0 10px rgba(0,0,0,0.3)',
                            borderColor: isHomePageActive ? 'rgba(255,255,255,1)' : 'primary.main',
                        },
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                        }
                    }}
                    >
                    <UserAvatar user={user} size={36} isMobile={false} retryImage={true}/>
                </Box>

                {profilePopoverOpen ? <>
                    <Backdrop
                        open
                        onClick={handleProfileClose}
                        sx={{
                            zIndex: 1100,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        }}
                    />
                    <Popover
                        open={profilePopoverOpen}
                        anchorEl={profileAnchorEl}
                        onClose={handleProfileClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        sx={{
                            marginTop: '10px'
                        }}>

                        <Box className="scrollBox" sx={{
                            width: '280px', 
                            maxHeight: '370px',
                            marginTop: '20px',
                            marginBottom: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            zIndex: 0,
                            position: 'relative'
                        }}>
                            <Profile />

                        </Box>

                    </Popover>
                </>:
                null }

                {Boolean(props.handbookMenuTab) && (<>
                    <Backdrop
                        open
                        onClick={() => props.setHandbookMenuTab(null)}
                        sx={{
                            zIndex: 1100,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        }}
                    />
                    <Popover
                        open={Boolean(props.handbookMenuTab)}
                        anchorEl={handbookButtonRef.current}
                        onClose={() => props.setHandbookMenuTab(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        sx={{
                            marginTop: '10px'
                        }}>

                        <Box className="scrollBox" sx={{
                            width: '300px', 
                            minHeight: '200px',
                            maxHeight: '400px',
                            marginBottom: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            zIndex: 0,
                            position: 'relative'
                        }}>
                            <HandbookMenu 
                                handbookMenuTab={props.handbookMenuTab}
                                onClose={() => props.setHandbookMenuTab(null)} />

                        </Box>

                    </Popover>
                </>)}

            </div>
        </div>
    )
}